import { inputDriverFactory as coreDriver } from '../internal/WixUiCoreComponents/components/CoreInput/CoreInput.driver';
import { tooltipDriverFactory } from '../internal/WixUiCoreComponents/components/CoreTooltip/CoreTooltip.driver';
import { EMPTY, ERROR, SUCCESS, THEME, DATA_HOOKS } from './dataKeys';
import { Simulate } from 'react-dom/test-utils';

export const textFieldDriverFactory = ({ element, eventTrigger }) => {
  const inputDriver = coreDriver({ element, eventTrigger });
  const getErrorTooltipDriver = () =>
    tooltipDriverFactory({
      element: element.querySelector(`[data-hook="${DATA_HOOKS.ERROR_ICON}"]`),
      eventTrigger,
    });

  function getDataAttributeValue(attributeName) {
    return inputDriver.getInput().getAttribute(attributeName);
  }

  function getDataAttributeBooleanValue(attributeName) {
    return getDataAttributeValue(attributeName) === 'true';
  }

  function getErrorMessage() {
    const ariaDescribedByArr =
      getDataAttributeValue('aria-describedby').split(' ');
    const errorAriaDescribedBy = ariaDescribedByArr.find(
      (val) => val.indexOf('error-message') > -1,
    );
    const tooltipContent =
      element.querySelector(`#${errorAriaDescribedBy}`) ||
      document.querySelector(`#${errorAriaDescribedBy}`);

    return tooltipContent ? tooltipContent.textContent : '';
  }

  function getThemeValue() {
    return getDataAttributeValue(THEME);
  }

  function getLabelText() {
    return element.querySelector(`[data-hook="${DATA_HOOKS.LABEL}"]`)
      .textContent;
  }

  function getFloatingLabelText() {
    return element.querySelector(`[data-hook="${DATA_HOOKS.FLOATING_LABEL}"]`)
      .textContent;
  }

  function getLabelForInput() {
    return element
      .querySelector(`[data-hook="${DATA_HOOKS.LABEL}"]`)
      .getAttribute('for');
  }

  function getInputId() {
    return inputDriver.getId();
  }

  function getLang() {
    return element.getAttribute('lang');
  }

  function isLabelExists() {
    return !!element.querySelector(`[data-hook="${DATA_HOOKS.LABEL}"]`);
  }

  function isFloatingLabelExists() {
    return !!element.querySelector(
      `[data-hook="${DATA_HOOKS.FLOATING_LABEL}"]`,
    );
  }

  function isLabelPlaceHolderExists() {
    return !!element.querySelector(
      `[data-hook="${DATA_HOOKS.LABEL_PLACEHOLDER}"]`,
    );
  }

  function isSuccessValue() {
    return getDataAttributeBooleanValue(SUCCESS);
  }

  function isErrorValue() {
    return getDataAttributeBooleanValue(ERROR);
  }

  function isHasEmptyState() {
    return getDataAttributeBooleanValue(EMPTY);
  }

  function isSuccessIconExist() {
    return !!element.querySelector(`[data-hook="${DATA_HOOKS.SUCCESS_ICON}"]`);
  }

  function isFocused() {
    return inputDriver.getInput() === document.activeElement;
  }

  function isClearButtonExist() {
    return !!element.querySelector(`[data-hook="${DATA_HOOKS.CLEAR_BUTTON}"]`);
  }

  function isCustomSuffixExist() {
    return !!element.querySelector(`[data-hook="${DATA_HOOKS.CUSTOM_SUFFIX}"]`);
  }

  function isArrowIconsExist() {
    return !!element.querySelector(`[data-hook="${DATA_HOOKS.ARROW_ICONS}"]`);
  }

  function getUpArrowIconTabIndex() {
    return element
      .querySelector(
        `[data-hook="${DATA_HOOKS.ARROW_ICONS}"] button:first-child`,
      )
      .getAttribute('tabindex');
  }

  function getDownArrowIconTabIndex() {
    return element
      .querySelector(
        `[data-hook="${DATA_HOOKS.ARROW_ICONS}"] button:last-child`,
      )
      .getAttribute('tabindex');
  }

  function getHelperText() {
    return element.querySelector(`[data-hook="${DATA_HOOKS.HELPER_TEXT}"]`)
      .textContent;
  }

  return {
    ...inputDriver,
    getTheme() {
      return getThemeValue();
    },
    isSuccess() {
      return isSuccessValue();
    },
    isSuccessIconExist() {
      return isSuccessIconExist();
    },
    isError() {
      return isErrorValue();
    },
    hasErrorMessage() {
      return !!getErrorMessage();
    },
    getErrorMessage() {
      return getErrorMessage();
    },
    getLabelText() {
      return getLabelText();
    },
    getFloatingLabelText() {
      return getFloatingLabelText();
    },
    getLabelForInput() {
      return getLabelForInput();
    },
    getInputId() {
      return getInputId();
    },
    getLang() {
      return getLang();
    },
    isLabelExists() {
      return isLabelExists();
    },
    isFloatingLabelExists() {
      return isFloatingLabelExists();
    },
    isLabelPlaceHolderExists() {
      return isLabelPlaceHolderExists();
    },
    isFocused() {
      return isFocused();
    },
    getUpArrowIconTabIndex() {
      return getUpArrowIconTabIndex();
    },
    getDownArrowIconTabIndex() {
      return getDownArrowIconTabIndex();
    },
    hasEmptyState() {
      return isHasEmptyState();
    },
    hasClearButton() {
      return isClearButtonExist();
    },
    clickOnClearButton() {
      const clearButton = element.querySelector(
        `[data-hook="${DATA_HOOKS.CLEAR_BUTTON}"]`,
      );
      Simulate.click(clearButton);
    },
    hasCustomSuffix() {
      return isCustomSuffixExist();
    },

    hover() {
      return eventTrigger.mouseEnter(element);
    },

    focus() {
      return inputDriver.getInput().focus();
    },

    getText() {
      return inputDriver.getValue();
    },

    /**
     * Gets the max length
     * @return {Promise<string>}
     */
    getMaxLength() {
      return parseInt(getDataAttributeValue('maxLength'), 10);
    },

    /**
     * Whether the charCount exists and displayed
     * @return {Promise<boolean>}
     */
    isCharCountExist() {
      return !!element.querySelector(`[data-hook="${DATA_HOOKS.CHAR_COUNT}"]`);
    },

    /**
     * Gets the ratio of characters used and the total character limit
     * @return {Promise<string>}
     */
    getCharCountText() {
      return element.querySelector(`[data-hook="${DATA_HOOKS.CHAR_COUNT}"]`)
        .textContent;
    },

    /**
     * Is error tooltip shown
     * @return boolean
     */
    isErrorTooltipShown() {
      return getErrorTooltipDriver().isContentElementExists();
    },

    /**
     * Click on error icon
     * @return void
     */
    clickOnErrorIcon() {
      const errorButton = element.querySelector(
        `[data-hook="${DATA_HOOKS.ERROR_ICON}"] button`,
      );
      Simulate.click(errorButton);
    },

    /**
     * Get prefix displayed
     * @return Node
     */
    getPrefix() {
      return element.querySelector(`[data-hook="${DATA_HOOKS.PREFIX}"]`);
    },

    /**
     * Get prefix label displayed
     * @return Node
     */
    getPrefixLabel() {
      return element.querySelector(`[data-hook="${DATA_HOOKS.PREFIX_LABEL}"]`);
    },

    /**
     * Return the element
     * @return node
     */
    element() {
      return element;
    },

    isArrowIconsExist() {
      return isArrowIconsExist();
    },

    getHelperText() {
      return getHelperText();
    },

    clickOnPasswordVisibilityButton() {
      const hideButton = element.querySelector(
        `[data-hook="${DATA_HOOKS.HIDDEN_ICON}"]`,
      );
      const showButton = element.querySelector(
        `[data-hook="${DATA_HOOKS.VISIBLE_ICON}"]`,
      );
      Simulate.click(hideButton ?? showButton);
    },
  };
};
